import RoutesHeader from '@/config/navigation/header'
import RoutesSite from '@/config/navigation/site'
import RoutesHidden from '@/config/navigation/hidden'
import { RoleHelper, useAuthStore, type WhoAmI } from '@/stores'
import {
  type _RouteLocationBase,
  createRouter,
  createWebHistory,
  type LocationAsRelativeRaw,
  type NavigationGuardWithThis,
  type RouteComponent,
  RouteLocationAsPath,
  type RouteLocationAsPathGeneric,
  RouteLocationAsRelative,
  type RouteLocationAsRelativeGeneric,
  RouteLocationAsString,
  type RouteLocationNormalized,
  type Router as VueRouter,
  type RouteRecordName,
  type RouteRecordRaw
} from 'vue-router'
import GuestLayout from '@/layouts/GuestLayout.vue'
import LoggedInLayout from '@/layouts/LoggedInLayout.vue'
import type { Props as PasswordResetViewProps } from '@/views/login/PasswordResetView.vue'
import { useLogger } from '@/composables/useLogger.ts'
import CmsService from '@/services/CmsService'
import { type Component, computed, ref, watchEffect } from 'vue'
import { useEventBus, useTitle } from '@vueuse/core'
import type { MenuItem } from 'primevue/menuitem'
import { useTitrop } from '@/composables/useTitrop.ts'
import { useAppState } from '@/composables/useAppState.ts' //see https://router.vuejs.org/guide/essentials/passing-props.html
//see https://router.vuejs.org/guide/essentials/passing-props.html
const NotFoundView = async () => import('@/views/NotFoundView.vue')
const ErrorView = async () => import('@/views/ErrorView.vue')
const LoginView = async () => import('@/views/login/LoginView.vue')
const PasswordResetView = async () => import('@/views/login/PasswordResetView.vue')
const AccessDeniedView = async () => import('@/views/AccessDeniedView.vue')
const KioskLayout = async () => import('@/layouts/KioskLayout.vue')
const NotImplementedView = async () => import('@/views/NotImplementedView.vue')
const KioskApprovalView = async () => import('@/views/kiosk/KioskApprovalView.vue')

const logger = useLogger('Router')
const r: VueRouter = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/index.html',
      redirect: { name: 'home' }
    },
    {
      //this is a special entry for site navigation (left side)
      name: '__site_navigation__',
      path: '/__site_navigation__',
      redirect: { name: 'home' },
      children: RoutesSite
    },
    {
      //this is a special entry for header navigation (top header dropdown)
      name: '__header_navigation__',
      path: '/__header_navigation__',
      redirect: { name: 'home' },
      children: RoutesHeader
    },
    {
      //this is a special entry for hidden navigation (partially features, tests, and so on)
      name: '__hidden_navigation__',
      path: '/__hidden_navigation__',
      redirect: { name: 'home' },
      children: RoutesHidden
    },
    {
      //this is a special entry for special routes
      name: '__special_routes',
      path: '/__special_routes',
      redirect: { name: 'home' },
      children: [
        {
          path: '/login/reset-password/:verificationcode?',
          name: 'login_reset_password',
          meta: { layout: GuestLayout },
          component: PasswordResetView,
          props: (route: _RouteLocationBase): PasswordResetViewProps => {
            return { verificationCode: route.params.verificationcode as string }
          }
        },
        {
          path: '/login',
          name: 'login',
          meta: { layout: GuestLayout },
          component: LoginView
        },
        {
          path: '/error',
          name: 'error',
          component: ErrorView
        },
        {
          path: '/access_denied',
          name: 'access_denied',
          component: AccessDeniedView,
          props: (route: _RouteLocationBase) => {
            return { route: route.redirectedFrom }
          }
        },
        {
          path: '/kiosk-request/:deviceid',
          name: 'kiosk-request',
          component: KioskApprovalView,
          props: (route: _RouteLocationBase): { deviceid: string } => {
            return { deviceid: route.params.deviceid.toString() }
          }
        },
        {
          path: '/kiosk',
          name: 'Kiosk',
          meta: { layout: KioskLayout },
          component: NotImplementedView
        },
        {
          path: '/:pathMatch(.*)*',
          name: 'NotFound',
          component: NotFoundView
        }
      ]
    }
  ]
})

const navigationTitleParts = ref<string[]>([])
const navigationTitleFixedIndex = ref(0)
watchEffect(() => {
  const fullTitle = navigationTitleParts.value.join(' - ')
  document.title = fullTitle
  useTitle(fullTitle)
})

export function SetPageTitle(...append: (string | undefined)[]) {
  const cleaned = append.filter((s) => !!s) as string[]
  if (cleaned.length) {
    navigationTitleParts.value = [...navigationTitleParts.value.slice(0, navigationTitleFixedIndex.value), ...cleaned]
  }
}

const layoutGuard: NavigationGuardWithThis<undefined> = (to: RouteLocationNormalized): void => {
  if (!to.meta.layout) to.meta.layout = LoggedInLayout
}
const titleGuard: NavigationGuardWithThis<undefined> = (to: RouteLocationNormalized): void => {
  const partials = ['Titrop']
  for (const matchedItem of to.matched) {
    if (!matchedItem.name?.toString().startsWith('_') && matchedItem.meta.title) partials.push(matchedItem.meta.title)
  }
  navigationTitleParts.value = partials
  navigationTitleFixedIndex.value = partials.length
}

export type SecurityCallback = ((roleHelper: RoleHelper, whoami: WhoAmI) => Awaited<boolean>) | undefined

export type SecuredRoute = RouteRecordRaw & {
  name?: RouteRecordName | null
  fullPath?: string
  path?: string
  meta?: { security?: SecurityCallback; [key: string]: unknown }
}

const securityGuard: NavigationGuardWithThis<undefined> = (to: RouteLocationNormalized): LocationAsRelativeRaw | undefined => {
  if (!EvaluateRouteSecurity(to)) return { name: 'access_denied', params: { route: JSON.stringify(to) } }
}

export function EvaluateRouteSecurity(route: SecuredRoute | RouteLocationNormalized) {
  if (!route.meta?.security) return true
  const titrop = useTitrop()
  const granted = route.meta.security(titrop.value.user.roles, titrop.value.user.current)
  if (route.name || route.fullPath) logger.debug(`security check: ${route.name?.toString() ?? route.path} => ${granted ? 'GRANTED' : 'DENIED'}`)
  return granted
}

const authenticationGuard: NavigationGuardWithThis<undefined> = async (to: RouteLocationNormalized): Promise<string | undefined> => {
  // redirect to login page if not logged in and trying to access a restricted page
  const publicPages: string[] = ['/login', '/login/reset-password', '/kiosk']
  const authRequired: boolean = !publicPages.includes(to.path) && !to.path.includes('/login')
  if (authRequired) {
    //the requested route requires authentication
    const authStore = useAuthStore()
    await authStore.refreshLoginState()
    if (!authStore.isLoggedIn) {
      authStore.returnUrl = to.fullPath
      return '/login'
    }
  } else {
    //NoOp - just let through as this is a public page
  }
}
const autoimported: Record<
    string,
    {
      default: RouteRecordRaw[]
    }
> = import.meta.glob('@/config/routes/**/*.{js,ts}', { eager: true })
for (const autoimportedKey in autoimported) {
  const groupKey: string = autoimportedKey.replace('/', '_')
  try {
    void autoimported[autoimportedKey].default
  } catch {
    console.log('error... reloading')
    location.reload()
  }
  r.addRoute({
    name: groupKey,
    path: '/' + groupKey,
    redirect: { name: 'home' },
    children: autoimported[autoimportedKey].default
  })
}

if (import.meta.env.DEV) {
  const securityIsDev = (roleHelper, _wai) => unref(roleHelper.developer) ?? false
  const debugRoot: RouteRecordRaw = {
    meta: { title: 'Dev-Views', markAsDev: true, security: securityIsDev },
    name: '__debug_routes__',
    path: '/nav_debug',
    children: []
  }

  const dbgViews = import.meta.glob('@/views/development/**/*.vue', { eager: true })
  const dbgViewsLog = {}
  for (const dbgView in dbgViews) {
    let name = dbgView.substring(23)
    name = name.substring(0, name.lastIndexOf('.'))
    if (name.endsWith('View')) name = name.substring(0, name.length - 4)
    const key = name.toLowerCase()
    const routeName = `debug_${key.replace('/', '_')}`
    debugRoot.children?.push({
      name: routeName,
      meta: {
        title: name,
        markAsDev: true,
        security: securityIsDev
      },
      path: '/debug/' + key,
      component: (dbgViews[dbgView] as { default: RouteComponent }).default
    })
    dbgViewsLog[name] = '/debug/' + key
  }

  logger.debug('Debug-Routes added', dbgViewsLog)

  const siteNavDebug = r.getRoutes().find((s) => s.name == '__site_navigation__')
  siteNavDebug?.children.push(debugRoot)
  r.addRoute(debugRoot)
}

//post-modify routes
// function _postConfigureRecursive() {}
//
// _postConfigureRecursive()

let cmsInjected = false
r.beforeResolve(async (to) => {
  const authStore = useAuthStore()
  if (!cmsInjected && authStore.isLoggedIn) {
    await CmsService.setup(r)
    cmsInjected = true
    //needs to redirect for new routes to work
    return r.resolve(to.fullPath)
  }
})
// order is important!
r.beforeEach(authenticationGuard)
r.beforeEach(securityGuard)
r.beforeEach(titleGuard)
r.beforeEach(layoutGuard)

const breadcrumbList = computed<(MenuItem & { iconComp?: Component })[]>(() => {
  return r.currentRoute.value.matched
      .filter((r) => !r.meta.hideInBreadcrumb && r.meta.title)
      .map((r) => ({
        label: r.meta.title,
        name: r.name,
        meta: r.meta,
        iconComp: r.meta.icon ?? undefined,
        url: r.path ?? undefined,
        r: r
      }))
})

useEventBus<string | RouteLocationAsRelativeGeneric | RouteLocationAsPathGeneric>('router.push').on((to) => {
  void r.push(to)
})
const generateFrontendUrlTo = (to?: RouteLocationAsString | RouteLocationAsRelative | RouteLocationAsPath): string => {
  const base = useAppState().envFrontend
  if (!to) return base.toString()
  return new URL(r.resolve(to).href, base).toString()
}

const generateFrontendUrl = (to?: string | URL): string => {
  const base = useAppState().envFrontend
  if (!to) return base.toString()
  return new URL(r.resolve(to).href, base).toString()
}

const generateBackendUrl = (...to: (string | URL)[]): string => {
  const base = useAppState().backendUrl.value
  if (!to || to.length == 0) return base.toString()
  return new URL(
      to
          .filter(Boolean)
          .map((segment) => segment.toString())
          .join('/'),
      base
  ).toString()
}
export const BreadcrumbList = breadcrumbList
export type RouterExtended = VueRouter & {
  generateFrontendUrlTo: typeof generateFrontendUrlTo
  generateFrontendUrl: typeof generateFrontendUrl
  generateBackendUrl: typeof generateBackendUrl
}
export const Router: RouterExtended = { ...r, generateFrontendUrlTo, generateFrontendUrl, generateBackendUrl }
